import { createRouter, createWebHistory, type NavigationFailure, type NavigationGuardWithThis, type NavigationHookAfter, type RouteLocation, type RouteLocationNormalized, type RouteLocationNormalizedLoaded, type RouteLocationRaw, type RouteRecordName, type RouteRecordNormalized, type RouteRecordRaw, type Router, type RouterOptions } from "vue-router";
import { useErrorNotifier } from "./errorHandling";
import type { ExtendedUserInfo } from "./auth";

let router: Router;
const configurers : ((routerBuilder: RouterBuilder) => void)[] = [];


export interface ProtectedRouteOptions {
    authReqs?: string[] | ((userInfo: ExtendedUserInfo, to: RouteLocationNormalized) => boolean);
}

export interface RouterBuilder extends Omit<Router,  "resolve" | "getRoutes" | "currentRoute" | "push" | "replace" | 
    "back" | "forward" | "go" | "isReady" | "install" | "addRoute" > {

    getRoutes(): Iterable<RouteRecordRaw> ;
    setDefaultParent(parent: RouteRecordName): void;
    addRoute(name: string, build:  (builder: RouteBuilder) => void): void;
    addBeforeBuildCallback(callback: (builder: RouterBuilder) => void): void;
    build(): Router;
}


class RouteBuilder {
    private _route: RouteRecordRaw;

    constructor(name: string) {
        this._route = {name: name, meta: {}} as RouteRecordRaw
        
    }

    allowAnonymous() {
        this._route.meta!.protection = undefined;
        return this;
    }

    withAuthorization(options? : ProtectedRouteOptions) {
        this._route.meta!.protection = options ?? {};
        return this;
    }

    isRoot() {
        this._route.meta!.root = true;
        return this;
    }

    withParent(parentName: RouteRecordName) {
        this._route.meta!.parent = parentName;
        return this;
    }

    withPath(path: string) {
        this._route.path = path;
        return this;
    }

    withComponent(component: any) {
        this._route.component = component;
        return this;
    }

    configure(callback: (builder: RouteRecordRaw) => void) {
        callback(this._route);
        return this;
    }

    withTitle(title: string) {
        this._route.meta!.title = title;
        return this;
    }

    build() {
        return this._route;
    }

    withChild(name: string, build: (builder: Omit<RouteBuilder, "withParent">) => void) {
        const builder = new RouteBuilder(name);
        build(builder);
        const route = builder.build();

        if (this._route.children == null) {
            this._route.children = [route];
        }
        else {
            this._route.children.push(route);
        }

        return this;
    }

    withLayout(layout: any) {
        this._route.meta!.layout = layout;
        return this;
    }
}

class RouterBuilderImpl implements RouterBuilder {
    private _router: Router;
    private _beforeBuildCallbacks: ((builder: RouterBuilder) => void)[];
    private _rootRoutes: Map<RouteRecordName, RouteRecordRaw> = new Map();
    private _allRoutes: Map<RouteRecordName, RouteRecordRaw> = new Map();
    private _defaultParent: RouteRecordName | undefined;

    constructor() {
        this._beforeBuildCallbacks = [];
        this._router = createRouter({
            history: createWebHistory(import.meta.env.BASE_URL), 
            routes: []
        });
    }

    setDefaultParent(parent: RouteRecordName): void {
        this._defaultParent = parent;
    }

    addBeforeBuildCallback(callback: (builder: RouterBuilder) => void): void {
        this._beforeBuildCallbacks.push(callback);
    }

    addRoute(name: string, build:  (builder: RouteBuilder) => void): void {
        const builder = new RouteBuilder(name);
        build(builder);
        const route = builder.build();

        if (route.meta?.root) {
            this._rootRoutes.set(route.name!, route);
        }
        else {
            const parent = this._rootRoutes.get(route.meta!.parent  as RouteRecordName ?? this._defaultParent);
            if (parent == null) {
                throw new Error(`Parent route ${route.meta?.parent} not found for route ${String(route.name)}`);
            }
            
            if (parent.children == null) {
                parent.children = [route];
            }
            else {
                parent.children.push(route);
            }
        }

        this._allRoutes.set(route.name!, route);
    }

    build(): Router {

        for (const callback of this._beforeBuildCallbacks) {
            callback(this);
        }

        for (const route of this._rootRoutes.values()) {
            this._router.addRoute(route);
        }

        return this._router;
    }

    get options() {
        return this._router.options;
    }

    get listening() {
        return this._router.listening;
    }
    

    removeRoute(name: RouteRecordName): void {
        const route = this._allRoutes.get(name);
        if (route == null) {
            throw new Error(`Route ${String(name)} not found`);
        }

        this._allRoutes.delete(name);
        if (route.meta?.root) {
            this._rootRoutes.delete(name);
        }
        else {
            const parent = this._allRoutes.get(route.meta!.parent as RouteRecordName ?? this._defaultParent)!;
            const index = parent.children?.findIndex(r => r.name == name)!;
            parent.children?.splice(index, 1);
        }
    }

    hasRoute(name: RouteRecordName): boolean {
        return this._allRoutes.has(name);
    }

    getRoutes(): Iterable<RouteRecordRaw> {
        return this._allRoutes.values()
    }

    

    beforeEach(guard: NavigationGuardWithThis<undefined>): () => void {
        return this._router.beforeEach(guard);
    }
    beforeResolve(guard: NavigationGuardWithThis<undefined>): () => void {
        return this._router.beforeResolve(guard);            
    }
    afterEach(guard: NavigationHookAfter): () => void {        
        return this._router.afterEach(guard);
    }
    onError(handler: (error: any, to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded) => any): () => void {
        return this._router.onError(handler);
    }

    private autoGenerateRouteName(route: RouteRecordRaw) {
        if (route.name == null) {
            route.name = route.path.replace(/\//g, "-");
        }

        return route;
    }
}

export function configureRouter(configurer: (router: RouterBuilder) => void) {
    if (router != null)
        throw new Error("Router already initialized");
    
    configurers.push(configurer); 
}

export function useRouter() {
    if (router == null) {
 
        const builder = new RouterBuilderImpl();
        builder.addBeforeBuildCallback(b => {
            for (const configurer of configurers) {
                configurer(builder);
            }
        })
        builder.afterEach(async (x: RouteLocationNormalizedLoaded) => {
            const titlePrefix = import.meta.env.VITE_TITLE_PREFIX ?? "";
            const rawTitle = (x.meta?.title as string | undefined) ?? "Portal";
            let title = rawTitle;
            try {
                const { useIntl } = await import("./i18n/i18nservice");
                const intl = useIntl();
                if (intl?.i18n?.te(rawTitle)) {
                    title = intl.i18n.t(rawTitle);
                }
            } catch {
                // i18n aún no inicializado
            }
            if (titlePrefix === "") {
                document.title = title;
            } else {
                document.title = `${titlePrefix} - ${title}`;
            }
        });
        router = builder.build();
        router.onError((err) => {
            useErrorNotifier().notifyError(err);
        });
    }

    return router;
}